import axiosInstance from 'Services/APIService';
import { UnitClass } from './UnitPurchaseTypes';

// ==================== FORMATTING UTILITIES ====================
export const formatCurrency = (amount: number): string => {
  if (typeof amount !== 'number' || isNaN(amount)) {
    return '$0';
  }

  const absAmount = Math.abs(amount);
  const isNegative = amount < 0;

  let formattedAmount: string;

  if (absAmount >= 1000000000) {
    formattedAmount = (absAmount / 1000000000).toFixed(3) + 'B';
  } else if (absAmount >= 1000000) {
    formattedAmount = (absAmount / 1000000).toFixed(3) + 'M';
  } else if (absAmount >= 1000) {
    formattedAmount = (absAmount / 1000).toFixed(3) + 'K';
  } else {
    formattedAmount = absAmount % 1 === 0 ? absAmount.toString() : absAmount.toFixed(3);
  }

  if (formattedAmount.includes('.')) {
    formattedAmount = formattedAmount.replace(/\.?0+$/, '');
  }

  const sign = isNegative ? '-' : '';
  return `${sign}$${formattedAmount}`;
};

export const getRiskColor = (riskLevel: string): string => {
  if (riskLevel.includes('Conservative')) return '#4caf50';
  if (riskLevel.includes('Moderate')) return '#ff9800';
  return '#f44336';
};

// ==================== LOCAL STORAGE UTILITIES ====================
export const getGroupId = (): string => {
  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
    projectId?: string;
    projectName?: string;
    isAdmin?: boolean;
  };
  return selectedProject?.projectId || '';
};

export const getLoggedInUser = () => {
  const loginInfo = localStorage.getItem('user-details');
  if (loginInfo) {
    try {
      const userDetails = JSON.parse(loginInfo);
      return {
        name: userDetails.name || '',
        email: userDetails.email || '',
        avatar: userDetails.avatar || ''
      };
    } catch (error) {
      console.error('Error parsing user details:', error);
      return { name: '', email: '', avatar: '' };
    }
  }
  return { name: '', email: '', avatar: '' };
};

// ==================== AVATAR UTILITIES ====================
export const fetchAvatarUrl = async (avatarPath: string): Promise<string | null> => {
  if (!avatarPath) return null;

  try {
    const response = await axiosInstance.get(
      `/avatar/get-avatar?path=${encodeURIComponent(avatarPath)}`,
      { responseType: 'blob' }
    );
    return URL.createObjectURL(response.data);
  } catch (error) {
    console.error('Error fetching avatar:', error);
    return null;
  }
};

// ==================== DATA PROCESSING UTILITIES ====================
export const processLandingData = (landingData: any[], landingDataStatus: string): UnitClass[] => {
  const initialUnitClasses: UnitClass[] = [
    {
      id: 'initial-income',
      name: 'Income Units',
      type: 'Income',
      unitPrice: 1.0,
      totalUnits: 0,
      totalNAV: 0,
      description:
        'Conservative investment focused on regular income generation through quarterly distributions',
      features: [
        'Regular quarterly distributions',
        'Lower volatility',
        'Stable income stream',
        'Conservative investment approach'
      ],
      riskLevel: 'Conservative',
      minimumInvestment: 10000,
      subscriptionType: 'incomeUnits'
    },
    {
      id: 'initial-growth',
      name: 'Growth Units',
      type: 'Growth',
      unitPrice: 1.0,
      totalUnits: 0,
      totalNAV: 0,
      description:
        'Growth-focused investment for long-term capital appreciation with higher risk tolerance',
      features: [
        'Capital growth focus',
        'Higher growth potential',
        'Reinvested earnings',
        'Long-term wealth building'
      ],
      riskLevel: 'Moderate to High',
      minimumInvestment: 15000,
      subscriptionType: 'growthUnits'
    }
  ];

  let processedData: UnitClass[] = [...initialUnitClasses];

  if (landingDataStatus === 'succeeded' && Array.isArray(landingData) && landingData.length > 0) {
    try {
      landingData.forEach((unit: any) => {
        const hasValidData =
          unit.hasOwnProperty('unitType') &&
          unit.hasOwnProperty('unitPrice') &&
          unit.unitPrice !== undefined &&
          unit.unitPrice !== null &&
          unit.unitPrice !== '' &&
          (unit.unitType === 'growthUnits' || unit.unitType === 'incomeUnits');

        if (hasValidData) {
          const totalUnits = parseFloat(String(unit.totalUnitsHoldTillDate)) || 0;
          const unitPrice = parseFloat(String(unit.unitPrice)) || 1.0;
          const totalNAV = parseFloat(String(unit.NAV)) || 0;
          const totalCashUsed = parseFloat(String(unit.totalCashUsed)) || 0;

          const isIncomeUnit = unit.unitType === 'incomeUnits';
          const unitType: 'Income' | 'Growth' = isIncomeUnit ? 'Income' : 'Growth';
          const unitName = isIncomeUnit ? 'Income Units' : 'Growth Units';

          const finalTotalNAV = totalCashUsed > 0 ? totalCashUsed : totalNAV;

          const processedUnit: UnitClass = {
            id: `live-${unit.unitType}`,
            name: unitName,
            type: unitType,
            unitPrice: unitPrice,
            totalUnits: totalUnits,
            totalNAV: finalTotalNAV,
            description: isIncomeUnit
              ? 'Conservative investment focused on regular income generation through quarterly distributions'
              : 'Growth-focused investment for long-term capital appreciation with higher risk tolerance',
            features: isIncomeUnit
              ? [
                  'Regular quarterly distributions',
                  'Lower volatility',
                  'Stable income stream',
                  'Conservative investment approach'
                ]
              : [
                  'Capital growth focus',
                  'Higher growth potential',
                  'Reinvested earnings',
                  'Long-term wealth building'
                ],
            riskLevel: isIncomeUnit ? 'Conservative' : 'Moderate to High',
            minimumInvestment: isIncomeUnit ? 10000 : 15000,
            subscriptionType: isIncomeUnit ? 'incomeUnits' : 'growthUnits'
          };

          const targetIndex = processedData.findIndex((item) => item.type === unitType);
          if (targetIndex !== -1) {
            processedData[targetIndex] = processedUnit;
            console.log(`Updated ${unitType} units with live data in purchase component`);
          }
        }
      });

      console.log('Final processed data for purchase:', processedData);
      return processedData;
    } catch (err) {
      console.error('Error processing API data:', err);
      return initialUnitClasses;
    }
  }

  console.log('Using initial unit classes for purchase - API not ready or no valid data');
  return initialUnitClasses;
};

// ==================== CAPACITY UTILITIES ====================
export const checkCapacitySufficient = (
  requestedAmount: number,
  availableCapacity: number
): boolean => {
  return requestedAmount <= availableCapacity;
};

export const getCapacityAfterPurchase = (
  requestedAmount: number,
  availableCapacity: number
): number => {
  return availableCapacity - requestedAmount;
};

// ==================== STATUS UTILITIES ====================
export const getDataSourceStatus = (
  loading: boolean,
  landingDataStatus: string,
  unitClasses: UnitClass[]
) => {
  if (loading || landingDataStatus === 'idle') {
    return { text: 'Loading fund data...', color: '#ff9800' };
  }

  if (landingDataStatus === 'failed') {
    return { text: 'Initial setup / Connection issue', color: '#f44336' };
  }

  if (landingDataStatus === 'succeeded') {
    const hasLiveData = unitClasses.some((unit) => unit.id.includes('live'));
    if (hasLiveData) {
      return { text: 'Live fund data', color: '#4caf50' };
    } else {
      return { text: 'Initial setup / First investor', color: '#2196f3' };
    }
  }

  return { text: 'Initial setup', color: '#2196f3' };
};
